import React, { useState, useEffect, useRef } from "react";
import Favorites from "./Favorites";
import OtherTools from "./OtherTools";

// Вспомогательные функции
const fonts = ["Poppins", "Roboto", "Montserrat", "Lobster", "Open Sans", "Raleway", "Pacifico", "Playfair Display", "Oswald", "Bebas Neue"];
const randomHex = () => `#${Math.floor(Math.random() * 0xffffff).toString(16).padStart(6, "0")}`;
const random = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;

const getContrastYIQ = hex => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq >= 128 ? "#000" : "#fff";
};

// Функции форматирования кода
const formatHtml = (html) => {
  let formatted = '';
  let indent = '';
  const stack = [];
  
  html = html.replace(/></g, '>\n<');
  const lines = html.split('\n');
  
  for (let line of lines) {
    line = line.trim();
    if (!line) continue;
    
    if (line.startsWith('</')) {
      indent = '  '.repeat(stack.length - 1);
      formatted += indent + line + '\n';
      stack.pop();
    } else if (line.endsWith('/>') || line.includes('<!')) {
      formatted += indent + line + '\n';
    } else if (line.startsWith('<')) {
      formatted += indent + line + '\n';
      if (!line.includes('</') && !line.endsWith('/>') && !line.includes('<!')) {
        stack.push(line);
        indent = '  '.repeat(stack.length);
      }
    } else {
      formatted += indent + line + '\n';
    }
  }
  
  return formatted.trim();
};

const formatCss = (css) => {
  let formatted = '';
  let indent = '';
  let inRule = false;
  
  const lines = css.split('\n');
  
  for (let line of lines) {
    line = line.trim();
    if (!line) continue;
    
    if (line.includes('{')) {
      formatted += indent + line + '\n';
      indent += '  ';
      inRule = true;
    } else if (line.includes('}')) {
      indent = indent.slice(2);
      formatted += indent + line + '\n';
      inRule = false;
    } else {
      formatted += indent + line + '\n';
    }
  }
  
  return formatted.trim();
};

const formatJs = (js) => {
  let formatted = '';
  let indent = '';
  
  const lines = js.split('\n');
  
  for (let line of lines) {
    line = line.trim();
    
    if (line.includes('}') && !line.includes('{')) {
      indent = indent.slice(2);
    }
    
    formatted += indent + line + '\n';
    
    if (line.includes('{') && !line.includes('}')) {
      indent += '  ';
    }
  }
  
  return formatted.trim();
};

// PIXABAY API константа
const PIXABAY_API_KEY = '24435876-c6e424432cb241a44895fddaf';

// Функции для работы с изображениями
const getPixabayImages = async (searchQuery, count = 5) => {
  try {
    const query = searchQuery.trim();
    console.log(`🔍 Pixabay запрос: "${query}"`);
    
    const url = `https://pixabay.com/api/?key=${PIXABAY_API_KEY}&q=${encodeURIComponent(query)}&image_type=photo&per_page=30&safesearch=true&order=popular&min_width=800&min_height=600`;
    
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`Pixabay API error: ${response.status}`);
    }
    
    const data = await response.json();
    
    if (data.hits && data.hits.length > 0) {
      console.log(`📊 Pixabay вернул ${data.hits.length} изображений по запросу: "${query}"`);
      
      const sortedHits = data.hits.sort((a, b) => {
        const scoreA = (a.likes || 0) * 2 + (a.imageWidth * a.imageHeight) / 100000;
        const scoreB = (b.likes || 0) * 2 + (b.imageWidth * b.imageHeight) / 100000;
        return scoreB - scoreA;
      });
      
      const validImages = sortedHits
        .filter((hit, index) => {
          const hasLarge = hit.largeImageURL && hit.largeImageURL.startsWith('https://');
          const hasWeb = hit.webformatURL && hit.webformatURL.startsWith('https://');
          
          if (!hasLarge && !hasWeb) {
            return false;
          }
          
          if (hit.imageWidth < 800 || hit.imageHeight < 600) {
            return false;
          }
          
          return true;
        })
        .slice(0, count)
        .map((hit, index) => {
          let imageUrl;
          if (index === 0 && hit.largeImageURL) {
            imageUrl = hit.largeImageURL;
          } else {
            imageUrl = hit.webformatURL || hit.largeImageURL;
          }
          
          if (imageUrl && imageUrl.startsWith('http://')) {
            imageUrl = imageUrl.replace('http://', 'https://');
          }
          
          return {
            url: imageUrl,
            alt: hit.tags || `Изображение "${query}" ${index + 1}`,
            validated: true,
            source: 'pixabay',
            width: hit.imageWidth,
            height: hit.imageHeight,
            likes: hit.likes || 0,
            views: hit.views || 0,
            tags: hit.tags || ''
          };
        });
      
      const resultImages = [];
      for (let i = 0; i < count; i++) {
        if (i < validImages.length) {
          resultImages.push(validImages[i]);
        } else if (validImages.length > 0) {
          resultImages.push({
            ...validImages[validImages.length - 1],
            alt: `${validImages[validImages.length - 1].alt} (${i + 1})`
          });
        }
      }
      
      return resultImages;
      
    } else {
      const fallbackQuery = query.split(' ').slice(0, 2).join(' ');
      if (fallbackQuery !== query) {
        console.log(`🔄 Пробую fallback запрос: "${fallbackQuery}"`);
        return getPixabayImages(fallbackQuery, count);
      }
      throw new Error(`Pixabay не вернул изображения для запроса: "${query}"`);
    }
    
  } catch (error) {
    console.error('Ошибка Pixabay API:', error);
    throw new Error(`Не удалось получить изображения: ${error.message}`);
  }
};

const determineThemeAndSearchQuery = async (userRequest) => {
  try {
    const API_KEY = process.env.REACT_APP_OPENAI_API_KEY;
    
    if (!API_KEY) {
      throw new Error("API ключ не найден");
    }

    const response = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${API_KEY}`
      },
      body: JSON.stringify({
        model: "gpt-3.5-turbo",
        messages: [{
          role: "system",
          content: `Ты — эксперт по созданию сверхточных поисковых запросов для Pixabay. Создай САМЫЙ ТОЧНЫЙ поисковый запрос на английском языке. Максимум 3 слова!`
        }, {
          role: "user",
          content: `Создай точный запрос для изображений для сайта: "${userRequest}"`
        }],
        temperature: 0.2,
        max_tokens: 20
      })
    });

    if (!response.ok) {
      throw new Error(`AI API error: ${response.status}`);
    }

    const data = await response.json();
    let result = data.choices[0].message.content.trim();
    
    result = result.replace(/["']/g, '').trim();
    const words = result.split(' ').filter(w => w.length > 0);
    
    const threeWordQuery = words.slice(0, 3).join(' ');
    const twoWordQuery = words.slice(0, 2).join(' ') || threeWordQuery;
    
    return {
      mainQuery: threeWordQuery,
      altQuery: twoWordQuery,
      userRequest: userRequest
    };
    
  } catch (error) {
    console.error("Ошибка определения темы AI:", error);
    return { mainQuery: 'website design', altQuery: 'website', userRequest };
  }
};

const analyzeRequestIntelligently = (userRequest) => {
  const request = userRequest.toLowerCase().trim();
  
  const keywordMapping = {
    'кухн': 'kitchen interior modern',
    'мебел': 'furniture interior design',
    'авто': 'car automobile vehicle',
    'квартир': 'apartment interior modern',
    'ремонт': 'renovation interior design',
    'ресторан': 'restaurant interior dining',
    'салон': 'beauty salon interior',
    'образовани': 'education classroom learning',
    'сайт': 'website design computer',
    'клиник': 'clinic medical hospital',
    'фитнес': 'fitness gym workout',
    'отель': 'hotel interior room'
  };
  
  let mainQuery = '';
  for (const [rusWord, engQuery] of Object.entries(keywordMapping)) {
    if (request.includes(rusWord)) {
      mainQuery = engQuery;
      break;
    }
  }
  
  if (!mainQuery) {
    const words = request.split(/[\s,.-]+/).filter(w => w.length > 3);
    if (words.length > 0) {
      mainQuery = words.slice(0, 3).join(' ');
    }
  }
  
  if (!mainQuery) {
    mainQuery = 'business interior design';
  }
  
  const altWords = mainQuery.split(' ').slice(0, 2);
  const altQuery = altWords.join(' ') || mainQuery;
  
  return {
    mainQuery: mainQuery,
    altQuery: altQuery,
    userRequest: userRequest
  };
};

const searchRelevantImages = async (userRequest, count = 5) => {
  try {
    const searchData = await determineThemeAndSearchQuery(userRequest);
    
    let images = [];
    let usedQuery = '';
    
    try {
      images = await getPixabayImages(searchData.mainQuery, count);
      usedQuery = searchData.mainQuery;
    } catch (error) {
      try {
        images = await getPixabayImages(searchData.altQuery, count);
        usedQuery = searchData.altQuery;
      } catch (altError) {
        const fallbackData = analyzeRequestIntelligently(userRequest);
        usedQuery = fallbackData.mainQuery;
        images = await getPixabayImages(usedQuery, count);
      }
    }
    
    const validatedImages = images.map(img => {
      let url = img.url;
      
      if (url && url.startsWith('http://')) {
        url = url.replace('http://', 'https://');
      }
      
      if (!url || url === 'undefined' || url === 'null') {
        url = `https://via.placeholder.com/800x600/667eea/ffffff?text=${encodeURIComponent(usedQuery)}`;
      }
      
      return {
        ...img,
        url: url,
        validated: true
      };
    });
    
    return {
      images: validatedImages,
      hero: validatedImages[0]?.url || '',
      card1: validatedImages[1]?.url || validatedImages[0]?.url || '',
      card2: validatedImages[2]?.url || validatedImages[0]?.url || '',
      card3: validatedImages[3]?.url || validatedImages[0]?.url || '',
      about: validatedImages[4]?.url || validatedImages[0]?.url || '',
      searchQuery: usedQuery,
      validatedCount: validatedImages.length,
      originalRequest: userRequest
    };
    
  } catch (error) {
    console.error("❌ Ошибка поиска изображений:", error);
    const contextQuery = analyzeRequestIntelligently(userRequest).mainQuery;
    const gradientImages = Array.from({ length: 5 }, (_, i) => ({
      url: `https://via.placeholder.com/800x600/${randomHex().slice(1)}/ffffff?text=${encodeURIComponent(contextQuery + ' ' + (i + 1))}`,
      alt: `Изображение ${contextQuery} ${i + 1}`,
      validated: true,
      source: 'placeholder'
    }));
    
    return {
      images: gradientImages,
      hero: gradientImages[0]?.url,
      card1: gradientImages[1]?.url || gradientImages[0]?.url,
      card2: gradientImages[2]?.url || gradientImages[0]?.url,
      card3: gradientImages[3]?.url || gradientImages[0]?.url,
      about: gradientImages[4]?.url || gradientImages[0]?.url,
      searchQuery: contextQuery,
      validatedCount: gradientImages.length,
      originalRequest: userRequest
    };
  }
};

const callChatGPT = async (messages, setStatus, imageResult) => {
  try {
    const API_KEY = process.env.REACT_APP_OPENAI_API_KEY;
    
    if (!API_KEY) {
      throw new Error("API ключ не найден");
    }

    setStatus("generating_site");
    
    const systemPrompt = {
      role: "system",
      content: `Ты — профессиональный UI/UX дизайнер. Создай КРАСИВЫЙ, СОВРЕМЕННЫЙ сайт.
      
!!!Обязательно делай красивейшее адаптивное меню со стильными кнопками!!!

🎯 ЗАПРОС ПОЛЬЗОВАТЕЛЯ: ${imageResult.originalRequest}
🔍 ПОИСКОВЫЙ ЗАПРОС: "${imageResult.searchQuery}"
📸 ИСПОЛЬЗУЙ ЭТИ ИЗОБРАЖЕНИЯ:
• Главный баннер: ${imageResult.hero}
• Карточка 1: ${imageResult.card1}
• Карточка 2: ${imageResult.card2}
• Карточка 3: ${imageResult.card3}
• Изображение "О нас": ${imageResult.about}

🔧 СТРОГИЕ ТРЕБОВАНИЯ:
1. Header с логотипом (только текст)
2. Hero-секция с заголовком и кнопкой
3. 3-4 карточки услуг с изображениями
4. Секция "О нас" с изображением
5. Footer с контактами
6. Адаптивный дизайн
7. Современные тенденции
8. Обязательно делай красивейшее адаптивное меню со стильными кнопками


🎨 Отвечай ТОЛЬКО в формате JSON:
{
  "html": "...полный HTML...",
  "css": "...полный CSS...", 
  "js": "...полный JS...",
  "description": "Краткое описание",
  "theme": "${imageResult.searchQuery}",
  "color_scheme": "основные цвета"
}`
    };

    const response = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${API_KEY}`
      },
      body: JSON.stringify({
        model: "gpt-3.5-turbo",
        messages: [systemPrompt, ...messages.slice(-3)],
        temperature: 0.7,
        max_tokens: 4000,
        response_format: { type: "json_object" }
      })
    });

    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }

    const data = await response.json();
    setStatus("success");
    return data.choices[0].message.content;
  } catch (error) {
    console.error('ChatGPT API error:', error);
    setStatus("error");
    throw error;
  }
};

// Хук для сохранения истории
const useChatHistory = () => {
  const [history, setHistory] = useState(() => {
    try {
      const saved = localStorage.getItem('ai_website_chat_history');
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      return [];
    }
  });

  const saveMessage = (message) => {
    const newHistory = [...history, message];
    setHistory(newHistory);
    try {
      localStorage.setItem('ai_website_chat_history', JSON.stringify(newHistory));
    } catch (e) {
      console.error('Ошибка сохранения истории:', e);
    }
  };

  const clearHistory = () => {
    setHistory([]);
    localStorage.removeItem('ai_website_chat_history');
  };

  return { history, saveMessage, clearHistory };
};

// Основной компонент Tools
export default function Tools({ user, favorites, applyFavorite, deleteFavorite }) {
  const [toolsList, setToolsList] = useState(["aiPreview"]);
  
  const [chatMessages, setChatMessages] = useState([]);
  const [userInput, setUserInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [generatedHtml, setGeneratedHtml] = useState("");
  const [generatedCss, setGeneratedCss] = useState("");
  const [generatedJs, setGeneratedJs] = useState("");
  const [activeCodeTab, setActiveCodeTab] = useState("html");
  const [apiStatus, setApiStatus] = useState("idle");
  const chatContainerRef = useRef(null);
  
  const [formattedHtml, setFormattedHtml] = useState("");
  const [formattedCss, setFormattedCss] = useState("");
  const [formattedJs, setFormattedJs] = useState("");

  const { history, saveMessage, clearHistory } = useChatHistory();

  // Состояния для других инструментов (передаются в OtherTools)
  const [font, setFont] = useState("Poppins");
  const [palette, setPalette] = useState([]);
  const [customColor, setCustomColor] = useState("#00d4ff");
  const [gradient, setGradient] = useState("linear-gradient(135deg, #25400fff, #4d1991ff)");
  const [buttonFont, setButtonFont] = useState("Poppins");
  const [buttonGradient, setButtonGradient] = useState("linear-gradient(135deg, #667eea, #764ba2)");
  const [buttonBorderRadius, setButtonBorderRadius] = useState(20);
  const [buttonBoxShadow, setButtonBoxShadow] = useState("0 15px 35px rgba(102,126,234,0.4)");
  const [buttonTextColor, setButtonTextColor] = useState("#ffffff");
  const [codeEditor, setCodeEditor] = useState(false);

  useEffect(() => {
    if (history.length > 0) {
      setChatMessages(history);
      
      const lastAssistantMsg = history
        .filter(msg => msg.role === "assistant" && msg.codeContext)
        .pop();
      
      if (lastAssistantMsg?.codeContext) {
        setGeneratedHtml(lastAssistantMsg.codeContext.html || "");
        setGeneratedCss(lastAssistantMsg.codeContext.css || "");
        setGeneratedJs(lastAssistantMsg.codeContext.js || "");
      }
    } else {
      const welcomeMessage = {
        role: "assistant",
        content: `Опишите, какой сайт вы хотите создать.`
      };
      setChatMessages([welcomeMessage]);
      saveMessage(welcomeMessage);
    }
  }, []);

  useEffect(() => {
    if (generatedHtml) setFormattedHtml(formatHtml(generatedHtml));
    if (generatedCss) setFormattedCss(formatCss(generatedCss));
    if (generatedJs) setFormattedJs(formatJs(generatedJs));
  }, [generatedHtml, generatedCss, generatedJs]);

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [chatMessages, isLoading]);

  const showToast = (msg, type = "success") => {
    const event = new CustomEvent("showToast", { detail: { msg, type } });
    window.dispatchEvent(event);
  };

  const copyToClipboard = text => {
    navigator.clipboard.writeText(text).then(() => showToast("Код скопирован!")).catch(() => showToast("Ошибка", "error"));
  };

  const generatePalette = () => {
    const baseHue = random(0, 360);
    const newPalette = Array.from({ length: 6 }, (_, i) => 
      `rgb(${Math.round(255 * Math.random())}, ${Math.round(255 * Math.random())}, ${Math.round(255 * Math.random())})`
    );
    setPalette(newPalette);
  };

  const generateFont = () => setFont(fonts[random(0, fonts.length - 1)]);
  
  const generateRandomButton = () => {
    const randomFontIndex = random(0, fonts.length - 1);
    setButtonFont(fonts[randomFontIndex]);
    const c1 = randomHex();
    const c2 = randomHex();
    setButtonGradient(`linear-gradient(135deg, ${c1}, ${c2})`);
    setButtonBorderRadius(random(12, 40));
    setButtonBoxShadow(`0 ${random(10,30)}px ${random(30,60)}px ${randomHex()}40`);
  };

  const toggleTool = tool => {
    setToolsList(prev => {
      if (prev.includes(tool)) {
        return prev.filter(t => t !== tool);
      } else {
        return [...prev, tool];
      }
    });
  };

  const sendMessage = async () => {
    if (!userInput.trim() || isLoading) return;

    const userMessage = {
      role: "user",
      content: userInput,
      timestamp: new Date().toISOString()
    };

    setChatMessages(prev => [...prev, userMessage]);
    saveMessage(userMessage);
    setUserInput("");
    setIsLoading(true);

    try {
      setApiStatus("creating_queries");
      const assistantThinkingMsg = {
        role: "assistant",
        content: "Разбираю тему...",
        timestamp: new Date().toISOString()
      };
      setChatMessages(prev => [...prev, assistantThinkingMsg]);
      
      let imageResult;
      try {
        imageResult = await searchRelevantImages(userInput);
        
        const queryMsg = {
          role: "assistant",
          content: `Думаю над содержимым вашего сайта!`,
          timestamp: new Date().toISOString()
        };
        setChatMessages(prev => {
          const filtered = prev.filter(msg => msg.content !== "Подбираю изображения");
          return [...filtered, queryMsg];
        });
        
      } catch (imageError) {
        console.error("Ошибка получения изображений:", imageError);
        const contextQuery = analyzeRequestIntelligently(userInput).mainQuery;
        imageResult = {
          images: [],
          hero: `https://via.placeholder.com/1200x800/667eea/ffffff?text=${encodeURIComponent(contextQuery)}`,
          card1: `https://via.placeholder.com/800x600/764ba2/ffffff?text=${encodeURIComponent(contextQuery + ' 1')}`,
          card2: `https://via.placeholder.com/800x600/ff6b6b/ffffff?text=${encodeURIComponent(contextQuery + ' 2')}`,
          card3: `https://via.placeholder.com/800x600/4ecdc4/ffffff?text=${encodeURIComponent(contextQuery + ' 3')}`,
          about: `https://via.placeholder.com/800x600/45b7d1/ffffff?text=About+${encodeURIComponent(contextQuery)}`,
          searchQuery: contextQuery,
          validatedCount: 5,
          originalRequest: userInput
        };
      }
      
      const aiResponse = await callChatGPT([userMessage], setApiStatus, imageResult);
      
      try {
        const parsedResponse = JSON.parse(aiResponse);
        if (parsedResponse.html && parsedResponse.css) {
          setGeneratedHtml(formatHtml(parsedResponse.html));
          setGeneratedCss(formatCss(parsedResponse.css));
          setGeneratedJs(formatJs(parsedResponse.js || "// JavaScript код будет здесь"));
          
          const assistantMessage = {
            role: "assistant",
            content: `Сайт создан!`,
            codeContext: {
              html: parsedResponse.html,
              css: parsedResponse.css,
              js: parsedResponse.js || "",
              theme: parsedResponse.theme || imageResult.searchQuery,
              pixabayQuery: imageResult.searchQuery,
              timestamp: new Date().toISOString()
            },
            timestamp: new Date().toISOString()
          };
          
          setChatMessages(prev => {
            const filtered = prev.filter(msg => !msg.content.includes("ChatGPT создал ТОЧНЫЙ запрос"));
            return [...filtered, assistantMessage];
          });
          saveMessage(assistantMessage);
          
          if (!toolsList.includes("aiPreview")) {
            setToolsList(prev => [...prev, "aiPreview"]);
          }
          
          setCodeEditor(true);
          setActiveCodeTab("html");
        }
      } catch (parseError) {
        console.error('Ошибка парсинга ответа AI:', parseError);
        const assistantMessage = {
          role: "assistant",
          content: "⚠️ **Ошибка обработки ответа AI**\nПопробуйте описать сайт более конкретно.",
          timestamp: new Date().toISOString()
        };
        setChatMessages(prev => {
          const filtered = prev.filter(msg => !msg.content.includes("ChatGPT создал ТОЧНЫЙ запрос"));
          return [...filtered, assistantMessage];
        });
        saveMessage(assistantMessage);
      }
    } catch (error) {
      console.error('Ошибка:', error);
      const assistantMessage = {
        role: "assistant",
        content: `❌ **Ошибка**\n${error.message}\n\nПопробуйте другой запрос или проверьте соединение.`,
        timestamp: new Date().toISOString()
      };
      setChatMessages(prev => {
        const filtered = prev.filter(msg => !msg.content.includes("ChatGPT создает ТОЧНЫЙ поисковый запрос"));
        return [...filtered, assistantMessage];
      });
      saveMessage(assistantMessage);
    } finally {
      setIsLoading(false);
      setApiStatus("idle");
    }
  };

  const startNewConversation = () => {
    if (window.confirm("Начать новый диалог? Текущая история будет очищена.")) {
      clearHistory();
      setChatMessages([{
        role: "assistant",
        content: `Опишите, какой сайт вы хотите создать.\n\n `,
        timestamp: new Date().toISOString()
      }]);
      setGeneratedHtml("");
      setGeneratedCss("");
      setGeneratedJs("");
      showToast("Начат новый диалог", "info");
    }
  };

  const getGoogleFontsUrl = () => {
    const matches = [...generatedCss.matchAll(/font-family:\s*['"]?([^,'";}]+)['"]?/gi)]
      .map(m => m[1].trim())
      .filter(f => !/^(sans-serif|serif|monospace)$/i.test(f));
    const all = [...new Set([font, buttonFont, ...matches])];
    return all.length 
      ? `https://fonts.googleapis.com/css2?${all.map(f => `family=${encodeURIComponent(f)}:wght@300;400;500;600;700&display=swap`).join("&")}` 
      : "";
  };

  const getActiveCode = () => {
    switch(activeCodeTab) {
      case "html": return formattedHtml;
      case "css": return formattedCss;
      case "js": return formattedJs;
      default: return "";
    }
  };

  const handleCodeChange = (value) => {
    switch(activeCodeTab) {
      case "html": 
        setGeneratedHtml(value);
        setFormattedHtml(formatHtml(value));
        break;
      case "css": 
        setGeneratedCss(value);
        setFormattedCss(formatCss(value));
        break;
      case "js": 
        setGeneratedJs(value);
        setFormattedJs(formatJs(value));
        break;
    }
  };

  const cardStyle = {
    background: "linear-gradient(145deg, #170026ff, #111111)",
    borderRadius: "24px",
    padding: "22px",
    boxShadow: "0 20px 50px rgba(0,0,0,0.6)",
    border: "1px solid #333",
    position: "relative",
    color: "#fff",
    width: "100%",
    maxWidth: "400px",
    boxSizing: "border-box",
    transition: "all 0.3s ease",
  };

  return (
    <div style={{ padding: "20px 32px", margin: "0 auto", fontFamily: "'Poppins', sans-serif" }}>
      <div style={{ marginBottom: "40px", display: "flex", gap: "16px", flexWrap: "wrap", justifyContent: "center" }}>
        {["font", "colorPicker", "gradient", "aiPreview", "palette", "buttonGenerator"].map(t =>
          !toolsList.includes(t) && (
            <button key={t} onClick={() => toggleTool(t)}>
              + {t === "aiPreview" ? "AI Website Generator" : t}
            </button>
          )
        )}
      </div>

      {toolsList.includes("aiPreview") && (
        <div style={{ 
          ...cardStyle, 
          maxWidth: "1000px",
          marginBottom: "28px",
          margin: "20px auto"
        }}>
          <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "10px" }}>
              <h2 style={{ margin: 0, color: "#fff" }}>AI Website Creator</h2>
              <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                <button 
                  onClick={startNewConversation}
                  style={{
                    padding: "10px 20px",
                    background: "rgba(255,100,100,0.2)",
                    borderRadius: "12px",
                    color: "#ffffffff",
                    cursor: "pointer",
                    fontWeight: "600",
                    fontSize: "14px"
                  }}
                >
                  Новый чат
                </button>
              </div>
            </div>

            {/* Чат интерфейс */}
            <div style={{ 
              height: "300px", 
              background: "rgba(0,0,0,0.3)", 
              borderRadius: "16px", 
              padding: "20px",
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              gap: "15px",
              border: "1px solid #333"
            }} ref={chatContainerRef}>
              {chatMessages.map((message, index) => (
                <div key={index} style={{
                  alignSelf: message.role === "user" ? "flex-end" : "flex-start",
                  background: message.role === "user" ? "linear-gradient(135deg, #667eea, #764ba2)" : "rgba(255,255,255,0.08)",
                  padding: "15px 20px",
                  borderRadius: "18px",
                  maxWidth: "85%",
                  color: "#fff",
                  wordWrap: "break-word",
                  border: message.role === "assistant" ? "1px solid rgba(255,255,255,0.1)" : "none",
                  lineHeight: "1.5"
                }}>
                  <div style={{ marginBottom: "5px", fontSize: "12px", opacity: 0.8, display: "flex", justifyContent: "space-between" }}>
                    <strong>{message.role === "user" ? "👤 Вы" : "🤖 AI Designer"}</strong>
                    {message.timestamp && (
                      <span style={{ fontSize: "11px", opacity: 0.6 }}>
                        {new Date(message.timestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                      </span>
                    )}
                  </div>
                  <div style={{ whiteSpace: "pre-wrap" }}>
                    {message.content}
                  </div>
                  {message.codeContext && (
                    <div style={{ 
                      marginTop: "8px", 
                      fontSize: "11px", 
                      opacity: 0.7,
                      paddingTop: "8px",
                      borderTop: "1px dashed rgba(255,255,255,0.1)"
                    }}>
                      🔍 Точный запрос: {message.codeContext.pixabayQuery || message.codeContext.theme || 'не указан'}
                    </div>
                  )}
                </div>
              ))}
              
              {apiStatus === "creating_queries" && (
                <div style={{
                  alignSelf: "flex-start",
                  background: "rgba(255,255,255,0.08)",
                  padding: "15px 20px",
                  borderRadius: "18px",
                  color: "#fff",
                  border: "1px solid rgba(255,255,255,0.1)"
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <span>🤖 ChatGPT создает ТОЧНЫЙ поисковый запрос для Pixabay...</span>
                    <div style={{ display: "flex", gap: "3px" }}>
                      <div style={{ width: "8px", height: "8px", background: "#667eea", borderRadius: "50%", animation: "bounce 1s infinite" }}></div>
                      <div style={{ width: "8px", height: "8px", background: "#764ba2", borderRadius: "50%", animation: "bounce 1s infinite 0.2s" }}></div>
                      <div style={{ width: "8px", height: "8px", background: "#ffd166", borderRadius: "50%", animation: "bounce 1s infinite 0.4s" }}></div>
                    </div>
                  </div>
                </div>
              )}
              
              {apiStatus === "generating_site" && (
                <div style={{
                  alignSelf: "flex-start",
                  background: "rgba(255,255,255,0.08)",
                  padding: "15px 20px",
                  borderRadius: "18px",
                  color: "#fff",
                  border: "1px solid rgba(255,255,255,0.1)"
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <span>🚀 Создаю сайт с релевантными изображениями...</span>
                    <div style={{ display: "flex", gap: "3px" }}>
                      <div style={{ width: "8px", height: "8px", background: "#4ade80", borderRadius: "50%", animation: "bounce 1s infinite" }}></div>
                      <div style={{ width: "8px", height: "8px", background: "#3b82f6", borderRadius: "50%", animation: "bounce 1s infinite 0.2s" }}></div>
                      <div style={{ width: "8px", height: "8px", background: "#8b5cf6", borderRadius: "50%", animation: "bounce 1s infinite 0.4s" }}></div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Поле ввода */}
            <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
              <div style={{ flex: 1, position: "relative" }}>
                <input
                  type="text"
                  value={userInput}
                  onChange={(e) => setUserInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && !isLoading && sendMessage()}
                  placeholder={generatedHtml ? "Опишите изменения для сайта..." : "Опишите ваш новый сайт..."}
                  style={{
                    width: "100%",
                    padding: "18px 50px 18px 20px",
                    background: "rgba(255,255,255,0.05)",
                    border: "1px solid #444",
                    borderRadius: "16px",
                    color: "#fff",
                    fontSize: "16px",
                    outline: "none"
                  }}
                  disabled={isLoading}
                />
                <button 
                  onClick={sendMessage}
                  disabled={isLoading || !userInput.trim()}
                  style={{
                    position: "absolute",
                    right: "10px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: userInput.trim() && !isLoading ? "linear-gradient(135deg, #667eea, #764ba2)" : "#444",
                    border: "none",
                    borderRadius: "12px",
                    padding: "10px 20px",
                    color: "#fff",
                    fontWeight: "600",
                    cursor: userInput.trim() && !isLoading ? "pointer" : "not-allowed",
                    opacity: userInput.trim() && !isLoading ? 1 : 0.6
                  }}
                >
                  {isLoading ? "⌛" : generatedHtml ? "Обновить" : "Отправить"}
                </button>
              </div>
            </div>

            {/* Превью сгенерированного сайта */}
            {generatedHtml && (
              <>
                <div style={{ 
                  height: "500px", 
                  width: "100%",
                  border: "1px solid #444",
                  borderRadius: "16px",
                  overflow: "hidden"
                }}>
                  <iframe 
                    title="ai-website-preview"
                    srcDoc={`
                      <!DOCTYPE html>
                      <html>
                      <head>
                        <meta charset="utf-8">
                        <meta name="viewport" content="width=device-width,initial-scale=1">
                        <style>
                          body {
                            font-family: 'Poppins', sans-serif;
                            margin: 0;
                            padding: 0;
                            background: #f8f9fa;
                            min-height: 100vh;
                          }
                          img {
                            max-width: 100%;
                            height: auto;
                            display: block;
                            object-fit: cover;
                          }
                          img[src*="undefined"],
                          img:not([src]),
                          img[src=""],
                          img[src*="null"] {
                            display: none !important;
                          }
                          ${generatedCss}
                        </style>
                        ${getGoogleFontsUrl() ? `<link rel="stylesheet" href="${getGoogleFontsUrl()}">` : ''}
                        <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css">
                      </head>
                      <body>
                        ${generatedHtml}
                        <script>
                          ${generatedJs}
                        </script>
                      </body>
                      </html>
                    `}
                    style={{ width: "100%", height: "100%", border: "none" }}
                    sandbox="allow-scripts"
                  />
                </div>
                
                <div style={{ display: "flex", gap: "10px", justifyContent: "center", flexWrap: "wrap", marginTop: "15px" }}>
                  <button onClick={() => setCodeEditor(!codeEditor)}
                    style={{
                      padding: "10px 20px",
                      background: codeEditor ? "rgba(255,100,100,0.2)" : "rgba(100,100,255,0.2)",
                      border: codeEditor ? "1px solid #ff6464" : "1px solid #6464ff",
                      borderRadius: "12px",
                      color: codeEditor ? "#ff6464" : "#6464ff",
                      cursor: "pointer",
                      fontWeight: "600",
                      fontSize: "14px"
                    }}
                  >
                    {codeEditor ? "Скрыть редактор" : "Показать редактор"}
                  </button>
                </div>

                {codeEditor && generatedHtml && (
                  <div style={{
                    marginTop: "30px",
                    background: "rgba(0,0,0,0.4)",
                    borderRadius: "20px",
                    padding: "25px",
                    border: "1px solid #444"
                  }}>
                    <div style={{ display: "flex", gap: "12px", marginBottom: "20px", flexWrap: "wrap", justifyContent: "center" }}>
                      {["html", "css", "js"].map(tab => (
                        <button
                          key={tab}
                          onClick={() => setActiveCodeTab(tab)}
                          style={{
                            padding: "12px 30px",
                            border: "none",
                            borderRadius: "12px",
                            background: activeCodeTab === tab ? "linear-gradient(135deg, #667eea, #764ba2)" : "rgba(255,255,255,0.1)",
                            color: "#fff",
                            fontWeight: "bold",
                            cursor: "pointer",
                            minWidth: "110px"
                          }}
                        >
                          {tab.toUpperCase()}
                        </button>
                      ))}
                    </div>

                    <textarea
                      value={getActiveCode()}
                      onChange={(e) => handleCodeChange(e.target.value)}
                      style={{
                        width: "100%",
                        height: "420px",
                        background: "#0d1117",
                        borderRadius: "16px",
                        padding: "20px",
                        fontFamily: "'Fira Code', 'Courier New', monospace",
                        fontSize: "14px",
                        color: "#ab6fecff",
                        border: "1px solid #30363d",
                        outline: "none",
                        resize: "vertical",
                        lineHeight: "1.6",
                        whiteSpace: "pre",
                        overflow: "auto"
                      }}
                      spellCheck={false}
                      placeholder={`Введите ${activeCodeTab.toUpperCase()} код здесь...`}
                    />

                    <div style={{ marginTop: "15px", display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: "10px", fontSize: "13px", color: "#888" }}>
                      <div>
                        Строк: <strong>{getActiveCode().split('\n').length}</strong> | 
                        Символов: <strong>{getActiveCode().length}</strong>
                      </div>
                      <div style={{ display: "flex", gap: "10px" }}>
                        <button onClick={() => copyToClipboard(getActiveCode())}
                          style={{ padding: "8px 16px", background: "rgba(255,255,255,0.1)", borderRadius: "8px", border: "none", color: "#fff", cursor: "pointer" }}>
                          Копировать {activeCodeTab.toUpperCase()}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Рендерим OtherTools для остальных инструментов */}
      <OtherTools 
        toolsList={toolsList.filter(tool => tool !== "aiPreview")}
        toggleTool={toggleTool}
        copyToClipboard={copyToClipboard}
        font={font}
        generateFont={generateFont}
        palette={palette}
        generatePalette={generatePalette}
        customColor={customColor}
        setCustomColor={setCustomColor}
        gradient={gradient}
        setGradient={setGradient}
        buttonFont={buttonFont}
        buttonGradient={buttonGradient}
        buttonTextColor={buttonTextColor}
        buttonBorderRadius={buttonBorderRadius}
        buttonBoxShadow={buttonBoxShadow}
        generateRandomButton={generateRandomButton}
      />
      
      <style>{`
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
        .close:hover {
          color: #ff4444;
          transform: scale(1.2);
        }
        button {
          background: #333;
          color: white;
          border: none;
          padding: 12px 24px;
          border-radius: 12px;
          cursor: pointer;
          font-weight: 600;
          transition: all 0.3s;
        }
        button:hover {
          background: #444;
          transform: translateY(-2px);
        }
      `}</style>
      
      <Favorites favorites={favorites} applyFavorite={applyFavorite} deleteFavorite={deleteFavorite} getContrastYIQ={getContrastYIQ} />
    </div>
  );
}